import * as React from "react";
import { Button, Text, Textarea, makeStyles } from "@fluentui/react-components";
import { Add24Regular, Subtract24Regular } from "@fluentui/react-icons";
import { parseFetchXmlToDraft } from "./buildFetchXml.js";
import type { FetchXmlBuilderTree } from "./types.js";

export interface QueryImportControlProps {
  onImportTree?: (tree: FetchXmlBuilderTree) => void;
  initiallyCollapsed?: boolean;
}

const useStyles = makeStyles({
  card: {
    border: "1px solid var(--colorNeutralStroke2)",
    borderRadius: "8px",
    padding: "12px",
    display: "grid",
    gap: "8px",
  },
  row: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
    flexWrap: "wrap",
  },
  editorRow: {
    display: "flex",
    gap: "8px",
    alignItems: "flex-start",
  },
  editorField: {
    flex: 1,
    minWidth: 0,
  },
  collapsedFieldLine: {
    flex: 1,
    minWidth: 0,
    height: 0,
    borderTop: "1px solid var(--colorNeutralStroke2)",
  },
  toggleButton: {
    flexShrink: 0,
    position: "relative",
    top: "-15px",
  },
  textarea: {
    width: "100%",
  },
  textareaInput: {
    minHeight: "520px",
    height: "520px",
  },
  errorText: {
    color: "var(--colorPaletteRedForeground1)",
  },
});

export function QueryImportControl({ onImportTree, initiallyCollapsed = true }: QueryImportControlProps): JSX.Element {
  const styles = useStyles();
  const [collapsed, setCollapsed] = React.useState(initiallyCollapsed);
  const [importXml, setImportXml] = React.useState("");
  const [importError, setImportError] = React.useState("");

  const onImportFetchXml = React.useCallback(() => {
    try {
      const parsed = parseFetchXmlToDraft(importXml);
      setImportError("");
      onImportTree?.(parsed);
    } catch (error) {
      setImportError(String(error));
    }
  }, [importXml, onImportTree]);

  const onFileSelected = React.useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    if (!file) {
      return;
    }

    try {
      const text = await file.text();
      setImportXml(text);
      setImportError("");
      setCollapsed(false);
    } catch (error) {
      setImportError(String(error));
    } finally {
      // Clear the picker so selecting the same file again still triggers onChange.
      event.currentTarget.value = "";
    }
  }, []);

  return (
    <div className={styles.card}>
      <div className={styles.row}>
        <Text weight="semibold">Import existing FetchXML</Text>
      </div>

      <div className={styles.row}>
        <input type="file" accept=".xml,text/xml" onChange={onFileSelected} />
      </div>

      <div className={styles.editorRow}>
        {collapsed ? (
          <div className={styles.collapsedFieldLine} aria-hidden="true" />
        ) : (
          <Textarea
            className={`${styles.editorField} ${styles.textarea}`}
            textarea={{ className: styles.textareaInput }}
            value={importXml}
            onChange={(_, data) => {
              setImportXml(data.value);
              setImportError("");
            }}
            placeholder="Paste existing FetchXML here"
          />
        )}

        <Button
          className={styles.toggleButton}
          appearance="subtle"
          icon={collapsed ? <Add24Regular /> : <Subtract24Regular />}
          aria-label={collapsed ? "Expand importer" : "Collapse importer"}
          title={collapsed ? "Expand" : "Collapse"}
          onClick={() => setCollapsed((value) => !value)}
        />
      </div>

      {!collapsed ? (
        <>
          <div className={styles.row}>
            <Button appearance="primary" onClick={onImportFetchXml} disabled={!importXml.trim()}>
              Import into builder
            </Button>
            <Button
              appearance="secondary"
              onClick={() => {
                setImportXml("");
                setImportError("");
              }}
              disabled={!importXml && !importError}
            >
              Clear
            </Button>
            {importError ? <Text className={styles.errorText}>{importError}</Text> : null}
          </div>
        </>
      ) : null}

      {collapsed && importError ? <Text className={styles.errorText}>{importError}</Text> : null}
    </div>
  );
}
